import React from 'react'
import { Check, Clock, X } from 'lucide-react'
import { CreditLimitRequest } from './types'

interface CreditRequestStatusPillProps {
    request: CreditLimitRequest
    fmt: (n: number) => string
}

const timeFmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

export default function CreditRequestStatusPill({ request, fmt }: CreditRequestStatusPillProps) {
    const who = request.requestedByRole ? `${request.requestedBy} · ${request.requestedByRole}` : request.requestedBy

    if (request.status === 'pending') {
        return (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                <Clock size={13} className="text-amber-500 mt-0.5 shrink-0 animate-pulse" strokeWidth={2} />
                <p className="text-[11.5px] text-amber-800 leading-snug">
                    <span className="font-semibold">Pending</span> — {who} requested {fmt(request.previousLimit)} → {fmt(request.newLimit)} at{' '}
                    {timeFmt(request.requestedAt)}
                </p>
            </div>
        )
    }

    if (request.status === 'approved') {
        return (
            <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                <Check size={13} className="text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} />
                <p className="text-[11.5px] text-emerald-800 leading-snug">
                    <span className="font-semibold">Approved</span> — {who} changed the limit to {fmt(request.newLimit)}
                    {request.resolvedAt ? ` at ${timeFmt(request.resolvedAt)}` : ''}
                </p>
            </div>
        )
    }

    return (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
            <X size={13} className="text-red-500 mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-[11.5px] text-red-700 leading-snug">
                <span className="font-semibold">Not applied</span> — {request.errorMessage ?? 'The request could not be completed.'}
            </p>
        </div>
    )
}
